"use client"

import { useTransition } from "react"
import { useRouter } from "next/navigation"
import { useLocale, useTranslations } from "next-intl"
import { Globe } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { LOCALES, LOCALE_NAMES } from "@/i18n/config"

/**
 * Chooses the language the APP is in.
 *
 * Lives in the app header, so it is reachable from every page — it began in the reader's
 * toolbar, which meant someone who wanted the interface in German had to open a judgment before
 * they could say so.
 *
 * Not the language the judgment is in. That is the reader's own FR/EN control, still in the
 * reader's toolbar, and the two are different questions with different answers. The Court publishes its
 * judgments in its own official versions and this corpus carries two of them; the app's chrome
 * is translated separately and is heading for all 24. Today both lists read "English, French",
 * which is exactly why each one says what it is switching.
 *
 * Every language is named IN ITSELF — "Français", not "French". A reader who has landed in the
 * wrong language cannot read a list written in the language they are trying to leave.
 *
 * `router.refresh()` rather than a state update: the locale is resolved per request on the
 * server (see i18n/request.ts), so the server components on the page — the reader's whole data
 * fetch among them — have to render again to come back in the new language.
 */
export function LanguageSwitcher() {
  const locale = useLocale()
  const router = useRouter()
  const t = useTranslations("nav")
  const [pending, startTransition] = useTransition()

  const choose = (next: string) => {
    if (next === locale) return
    startTransition(async () => {
      await fetch("/api/user/set-locale", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale: next }),
      })
      router.refresh()
    })
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon" disabled={pending} aria-label={t("appLanguage")}>
              <Globe className="stroke-[2]" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t("appLanguage")}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        {LOCALES.map((option) => (
          <DropdownMenuCheckboxItem
            key={option}
            checked={option === locale}
            onCheckedChange={() => choose(option)}
          >
            {LOCALE_NAMES[option]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
